import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Building2, Pencil, Plus, RotateCcw } from 'lucide-react';
import { listDepartments, retireDepartment, restoreDepartment } from '@/api/departments';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import type { Department } from '@/types/oscar';
import { DepartmentForm } from './DepartmentForm';
import { geographicOptions } from './schema';

/**
 * Department Code Builder (PR-27). Admin CRUD grid over oscar.Department.
 * Reads are open to any Oscar IT role; create/edit/retire/restore are
 * admin-only (tighter than the helpdesk-can-mutate pattern elsewhere — this
 * reference data drives every future onboarding allocation).
 */
export function DepartmentBuilder() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [geo, setGeo] = useState('');
  const [bu, setBu] = useState('');
  const [showRetired, setShowRetired] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);

  const [retireTarget, setRetireTarget] = useState<Department | null>(null);
  const [retireError, setRetireError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['departments', { includeDeleted: showRetired }],
    queryFn: ({ signal }) => listDepartments(showRetired, signal),
  });

  const departments = useMemo(() => query.data ?? [], [query.data]);

  // Business-unit filter options derived from the loaded data.
  const buOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of departments) map.set(d.BusinessUnitCode, d.BusinessUnitDescription);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [departments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return departments.filter((d) => {
      if (geo && d.GeographicCode !== geo) return false;
      if (bu && d.BusinessUnitCode !== bu) return false;
      if (q && !(`${d.DepartmentCode} ${d.DepartmentDescription}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [departments, search, geo, bu]);

  const retireMutation = useMutation({
    mutationFn: (code: string) => retireDepartment(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setRetireTarget(null);
      setRetireError(null);
    },
    onError: (err) => {
      // A 409 carries the "N position titles still assigned…" message — show it
      // inline in the dialog rather than as a generic error toast.
      setRetireError(err instanceof ApiError ? err.message : 'Could not retire the department code.');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (code: string) => restoreDepartment(code),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['departments'] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (d: Department) => { setEditing(d); setFormOpen(true); };
  const askRetire = (d: Department) => { setRetireError(null); setRetireTarget(d); };

  return (
    <section>
      <header className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-navy">
            <Building2 className="h-6 w-6 text-navy" aria-hidden /> Department Codes
          </h1>
          <p className="mt-1 text-sm text-darkGrey/80">
            The AD department-code master that drives onboarding allocation.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            <Plus className="h-4 w-4" aria-hidden /> Add department code
          </button>
        )}
      </header>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Geographic
          <select
            aria-label="Filter by geographic scope"
            value={geo}
            onChange={(e) => setGeo(e.target.value)}
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          >
            <option value="">All</option>
            {geographicOptions.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Business unit
          <select
            aria-label="Filter by business unit"
            value={bu}
            onChange={(e) => setBu(e.target.value)}
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          >
            <option value="">All</option>
            {buOptions.map(([code, desc]) => <option key={code} value={code}>{desc} ({code})</option>)}
          </select>
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input
            aria-label="Search department codes"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Code or description"
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          />
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-xs font-semibold text-darkGrey">
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(e) => setShowRetired(e.target.checked)}
            className="h-4 w-4"
          />
          Show retired
        </label>
      </div>

      <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">Geographic</th>
              <th className="px-3 py-2">Business unit</th>
              <th className="px-3 py-2">Sub-unit</th>
              {isAdmin && <th className="px-3 py-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan={isAdmin ? 6 : 5} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>
            )}
            {query.isError && (
              <tr><td colSpan={isAdmin ? 6 : 5} className="px-3 py-6 text-center text-red">Could not load department codes.</td></tr>
            )}
            {!query.isLoading && !query.isError && filtered.length === 0 && (
              <tr><td colSpan={isAdmin ? 6 : 5} className="px-3 py-6 text-center text-darkGrey/60">No department codes match the filters.</td></tr>
            )}
            {filtered.map((d) => (
              <tr key={d.DepartmentCode} className="border-t border-border hover:bg-bg/60">
                <td className="px-3 py-2">
                  <span className="font-mono font-medium text-navy">{d.DepartmentCode}</span>
                  {d.IsDeleted && (
                    <span className="ml-2 inline-flex rounded-full bg-darkGrey/10 px-2 py-0.5 text-xs font-semibold text-darkGrey">
                      Retired
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-darkGrey/90">{d.DepartmentDescription}</td>
                <td className="px-3 py-2 text-darkGrey/80">{d.GeographicDescription}</td>
                <td className="px-3 py-2 text-darkGrey/80">{d.BusinessUnitDescription}</td>
                <td className="px-3 py-2 text-darkGrey/80">{d.BUSubDescription ?? '—'}</td>
                {isAdmin && (
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {d.IsDeleted ? (
                        <button
                          type="button"
                          onClick={() => restoreMutation.mutate(d.DepartmentCode)}
                          className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-navy hover:bg-bg"
                        >
                          <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Restore
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            aria-label={`Edit ${d.DepartmentCode}`}
                            onClick={() => openEdit(d)}
                            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-navy hover:bg-bg"
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden /> Edit
                          </button>
                          <button
                            type="button"
                            aria-label={`Retire ${d.DepartmentCode}`}
                            onClick={() => askRetire(d)}
                            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-red hover:bg-red/10"
                          >
                            <Archive className="h-3.5 w-3.5" aria-hidden /> Retire
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-sm text-darkGrey/70">
        {filtered.length} of {departments.length} department code{departments.length === 1 ? '' : 's'}
      </p>

      {isAdmin && (
        <DepartmentForm open={formOpen} onOpenChange={setFormOpen} editing={editing} departments={departments} />
      )}

      {retireTarget && (
        <AlertDialog
          open={retireTarget !== null}
          onOpenChange={(o) => { if (!o) { setRetireTarget(null); setRetireError(null); } }}
          title={`Retire ${retireTarget.DepartmentCode}?`}
          description="The department code is soft-deleted and hidden from the register. You can restore it later."
          confirmLabel="Retire"
          destructive
          closeOnConfirm={false}
          onConfirm={() => retireMutation.mutate(retireTarget.DepartmentCode)}
        >
          {retireError && (
            <div role="alert" className="rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
              {retireError}{' '}
              <Link to="/onboarding/position-titles" className="font-semibold underline">
                Open the Position Title Allocator
              </Link>{' '}
              to re-home them first.
            </div>
          )}
        </AlertDialog>
      )}
    </section>
  );
}
